import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { submitFeedback } from '../../services/commonService';

const feedbackCategories = [
  { value: 'general', label: 'General' },
  { value: 'suggestion', label: 'Suggestion' },
  { value: 'bug', label: 'Bug' },
  { value: 'complaint', label: 'Complaint' }
];

export const FeedbackDialog = ({ open, onClose, userEmail }) => {
  const { t } = useTranslation('general');
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [feedback, setFeedback] = useState('');
  const [category, setCategory] = useState('general');
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!feedback.trim()) {
      setError(t('feedback.errors.emptyFeedback'));
      return;
    }
    if (feedback.split(' ').length > 300) {
      setError(t('feedback.errors.tooLong'));
      return;
    }
    
    setLoading(true);
    try {
      await submitFeedback({
        email: userEmail,
        category,
        feedback: feedback.trim()
      });
      
      setNotification({
        open: true,
        message: t('feedback.success'),
        severity: 'success'
      });
      handleClose();
    } catch (err) {
      setNotification({
        open: true,
        message: t('feedback.error'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFeedback('');
    setCategory('general');
    setError('');
    onClose();
  };

  const handleNotificationClose = () => {
    setNotification({ ...notification, open: false });
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        fullScreen={fullScreen}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: theme.shape.borderRadius,
            m: fullScreen ? 0 : 2
          }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {t('feedback.title')}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{ color: theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            select
            fullWidth
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            label={t('feedback.category')}
            margin="normal"
            sx={{ mb: 2 }}
          >
            {feedbackCategories.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(`feedback.categories.${option.value}`)}
              </MenuItem>
            ))}
          </TextField>
          
          <TextField
            fullWidth
            multiline
            rows={4}
            value={feedback}
            onChange={(e) => {
              setFeedback(e.target.value);
              setError('');
            }}
            label={t('feedback.message')}
            error={!!error}
            helperText={error || t('feedback.maxWords')}
            disabled={loading}
          />
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            {t('actions.cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {t('actions.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleNotificationClose} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};